// ShowerTypesSection.jsx
// Showers division (08 00 02) - Shower Types tab content

import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumericInput } from '../../components/NumericInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Trash2, Edit2, Upload, ClipboardPaste, Loader2, X, Image as ImageIcon } from 'lucide-react';
import ColorPickerPopover from '../../components/ColorPickerPopover';
import { getWarrantyError, hasWarrantyErrors } from '../../utils/validateWarranty';

const DEFAULT_MARKER_COLOR = '#10B981';

const OPERATION_TYPES = [
  'Sliding',
  'Hinged',
  'Pivot',
  'Fixed',
  'Bi-Fold',
  'Neo-Angle',
  'Corner Entry',
  'Walk-In',
];

export const ShowerTypesSection = ({
  // Data
  showerTypes = [],
  selectedShowerTypeIds = [],
  newShowerType,
  editingShowerType,
  showShowerTypeDialog,
  showerTypePictureUrl,
  pendingShowerTypePicture,
  showerTypePictureUploading,
  operationLoading,
  // Permissions
  canEditCSIItems,
  canUserEdit,
  // Handlers
  setNewShowerType,
  setShowShowerTypeDialog,
  setEditingShowerType,
  setShowerTypePictureUrl,
  setPendingShowerTypePicture,
  setShowerTypes,
  selectAllShowerTypes,
  confirmBulkDelete,
  toggleShowerTypeSelection,
  handleCreateShowerType,
  handleDeleteShowerType,
  handleDeleteShowerTypePicture,
  openEditShowerTypeDialog,
  resetShowerTypeDialog,
  onViewDrawing,
  onPasteFromClipboard,
  onPictureUpload,
  // API
  API,
  axios,
  toast,
}) => {
  // Reset form when dialog closes
  const handleDialogOpenChange = (open) => {
    if (open && !editingShowerType) {
      setShowerTypePictureUrl(null);
      if (pendingShowerTypePicture?.previewUrl) {
        URL.revokeObjectURL(pendingShowerTypePicture.previewUrl);
      }
      setPendingShowerTypePicture(null);
    }
    setShowShowerTypeDialog(open);
    if (!open) {
      resetShowerTypeDialog();
    }
  };

  const handleInputChange = (field, value) => {
    setNewShowerType(prev => ({ ...prev, [field]: value }));
  };

  // Color picker handlers
  const handleColorPickerOpenChange = (typeId, isOpen) => {
    setShowerTypes(prev => prev.map(t => 
      t.id === typeId ? { ...t, colorPickerOpen: isOpen } : t
    ));
  };

  const handleColorChange = (typeId, newColor) => {
    setShowerTypes(prev => prev.map(t =>
      t.id === typeId ? { ...t, pendingMarkerColor: newColor } : t
    ));
  };

  const handleColorSave = async (type) => {
    const newColor = type.pendingMarkerColor || type.markerColor;
    setShowerTypes(prev => prev.map(t =>
      t.id === type.id ? { ...t, savingColor: true } : t
    ));
    
    try {
      await axios.put(`${API}/shower-types/${type.id}`, {
        projectId: type.projectId,
        csiItemId: type.csiItemId,
        name: type.name,
        manufacturer: type.manufacturer,
        model: type.model || '',
        operationType: type.operationType,
        showerStyle: type.showerStyle || null,
        fgFinish: type.fgFinish || null,
        fgManufacturer: type.fgManufacturer || null,
        ucFinishType: type.ucFinishType || null,
        ucFinish: type.ucFinish || null,
        ucManufacturer: type.ucManufacturer || null,
        finishWarranty: type.finishWarranty,
        glassWarranty: type.glassWarranty,
        hardwareWarranty: type.hardwareWarranty,
        installationWarranty: type.installationWarranty,
        markerColor: newColor,
      });
      
      setShowerTypes(prev => prev.map(t =>
        t.id === type.id ? { 
          ...t, 
          markerColor: newColor, 
          pendingMarkerColor: null,
          colorPickerOpen: false,
          savingColor: false 
        } : t
      ));
      toast.success('Color saved');
    } catch (error) {
      toast.error('Failed to save color');
      setShowerTypes(prev => prev.map(t =>
        t.id === type.id ? { ...t, savingColor: false } : t
      ));
    }
  };

  // Paste from clipboard handler for modal
  const handlePasteFromClipboardInModal = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find(type => type.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], 'pasted-image.png', { type: imageType });
          const previewUrl = URL.createObjectURL(file);
          setPendingShowerTypePicture({ file, previewUrl });
          toast.success('Image pasted from clipboard');
          return;
        }
      }
      toast.error('No image found in clipboard');
    } catch (error) {
      toast.error('Failed to paste from clipboard');
    }
  };

  const isFormValid = () => {
    return newShowerType.name?.trim() && 
           newShowerType.manufacturer?.trim() && 
           newShowerType.operationType?.trim() &&
           !hasWarrantyErrors([newShowerType.finishWarranty, newShowerType.glassWarranty, newShowerType.hardwareWarranty, newShowerType.installationWarranty]);
  };

  return (
    <div data-testid="shower-types-section">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Shower Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && showerTypes.length > 0 && (
            <>
              <Button
                variant="outline"
                onClick={selectAllShowerTypes}
              >
                {selectedShowerTypeIds.length === showerTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                onClick={() => confirmBulkDelete('showerType')}
                disabled={selectedShowerTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                data-testid="bulk-delete-shower-types"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedShowerTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Dialog open={showShowerTypeDialog} onOpenChange={handleDialogOpenChange}>
              <DialogTrigger asChild>
                <Button data-testid="add-shower-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Shower Type
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingShowerType ? 'Edit Shower Type' : 'Create Shower Type'}</DialogTitle>
                  <DialogDescription>
                    {editingShowerType ? 'Update the shower type details' : 'Add a new shower type to this project'}
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleCreateShowerType} className="space-y-6">
                  {/* Color Picker - Top Right */}
                  <div className="flex justify-end">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Marker Color:</Label>
                      <div 
                        className="w-8 h-8 rounded cursor-pointer border-2 border-gray-300"
                        style={{ backgroundColor: newShowerType.markerColor || DEFAULT_MARKER_COLOR }}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'color';
                          input.value = newShowerType.markerColor || DEFAULT_MARKER_COLOR;
                          input.onchange = (e) => handleInputChange('markerColor', e.target.value);
                          input.click();
                        }}
                        title="Click to change marker color"
                      />
                    </div>
                  </div>

                  {/* Basic Info */}
                  <div className="space-y-4">
                    <h4 className="font-semibold border-b pb-2">Basic Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Shower Type *</Label>
                        <Input
                          value={newShowerType.name || ''}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="e.g., ST-1"
                          required
                          data-testid="shower-type-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Operation Type *</Label>
                        <Select
                          value={newShowerType.operationType || ''}
                          onValueChange={(value) => handleInputChange('operationType', value)}
                        >
                          <SelectTrigger data-testid="shower-operation-type-select">
                            <SelectValue placeholder="Select operation type" />
                          </SelectTrigger>
                          <SelectContent>
                            {OPERATION_TYPES.map((op) => (
                              <SelectItem key={op} value={op}>{op}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Manufacturer *</Label>
                        <Input
                          value={newShowerType.manufacturer || ''}
                          onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                          placeholder="e.g., Kohler"
                          required
                          data-testid="shower-manufacturer-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Model</Label>
                        <Input
                          value={newShowerType.model || ''}
                          onChange={(e) => handleInputChange('model', e.target.value)}
                          placeholder="e.g., Revel"
                          data-testid="shower-model-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Full Glass / U Channel Selection */}
                  <div className="space-y-4">
                    <h4 className="font-semibold border-b pb-2">Shower Style</h4>
                    <div className="space-y-3">
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="showerStyle"
                            value="Full Glass"
                            checked={newShowerType.showerStyle === 'Full Glass'}
                            onChange={(e) => handleInputChange('showerStyle', e.target.value)}
                            className="accent-orange-500"
                          />
                          <span className="text-sm font-medium">Full Glass</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="showerStyle"
                            value="U Channel"
                            checked={newShowerType.showerStyle === 'U Channel'}
                            onChange={(e) => handleInputChange('showerStyle', e.target.value)}
                            className="accent-orange-500"
                          />
                          <span className="text-sm font-medium">U Channel</span>
                        </label>
                      </div>
                      
                      {newShowerType.showerStyle === 'Full Glass' && (
                        <div className="grid grid-cols-2 gap-4 p-3 border rounded-md bg-slate-50">
                          <div className="space-y-2">
                            <Label>Finish</Label>
                            <Input
                              value={newShowerType.fgFinish || ''}
                              onChange={(e) => handleInputChange('fgFinish', e.target.value)}
                              placeholder="e.g., Chrome, Brushed Nickel"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Manufacturer</Label>
                            <Input
                              value={newShowerType.fgManufacturer || ''}
                              onChange={(e) => handleInputChange('fgManufacturer', e.target.value)}
                              placeholder="e.g., CRL, PPG"
                            />
                          </div>
                        </div>
                      )}
                      
                      {newShowerType.showerStyle === 'U Channel' && (
                        <div className="grid grid-cols-3 gap-4 p-3 border rounded-md bg-slate-50">
                          <div className="space-y-2">
                            <Label>Finish Type</Label>
                            <Input
                              value={newShowerType.ucFinishType || ''}
                              onChange={(e) => handleInputChange('ucFinishType', e.target.value)}
                              placeholder="e.g., Anodized, Painted"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Finish</Label>
                            <Input
                              value={newShowerType.ucFinish || ''}
                              onChange={(e) => handleInputChange('ucFinish', e.target.value)}
                              placeholder="e.g., Clear, Dark Bronze"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Manufacturer</Label>
                            <Input
                              value={newShowerType.ucManufacturer || ''}
                              onChange={(e) => handleInputChange('ucManufacturer', e.target.value)}
                              placeholder="e.g., CRL, Alumax"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Warranty */}
                  <div className="space-y-4">
                    <h4 className="font-semibold border-b pb-2">Warranty Information (Years)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Finish Warranty</Label>
                        <NumericInput
                          value={newShowerType.finishWarranty || ''}
                          onChange={(v) => handleInputChange('finishWarranty', v ? parseInt(v) : null)}
                          placeholder="e.g., 5"
                          className={getWarrantyError(newShowerType.finishWarranty) ? 'border-red-500' : ''}
                          data-testid="shower-finish-warranty-input"
                        />
                        {getWarrantyError(newShowerType.finishWarranty) && <p className="text-xs text-red-500">{getWarrantyError(newShowerType.finishWarranty)}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label>Glass Warranty</Label>
                        <NumericInput
                          value={newShowerType.glassWarranty || ''}
                          onChange={(v) => handleInputChange('glassWarranty', v ? parseInt(v) : null)}
                          placeholder="e.g., 10"
                          className={getWarrantyError(newShowerType.glassWarranty) ? 'border-red-500' : ''}
                          data-testid="shower-glass-warranty-input"
                        />
                        {getWarrantyError(newShowerType.glassWarranty) && <p className="text-xs text-red-500">{getWarrantyError(newShowerType.glassWarranty)}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label>Hardware Warranty</Label>
                        <NumericInput
                          value={newShowerType.hardwareWarranty || ''}
                          onChange={(v) => handleInputChange('hardwareWarranty', v ? parseInt(v) : null)}
                          placeholder="e.g., 3"
                          className={getWarrantyError(newShowerType.hardwareWarranty) ? 'border-red-500' : ''}
                          data-testid="shower-hardware-warranty-input"
                        />
                        {getWarrantyError(newShowerType.hardwareWarranty) && <p className="text-xs text-red-500">{getWarrantyError(newShowerType.hardwareWarranty)}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label>Installation Warranty</Label>
                        <NumericInput
                          value={newShowerType.installationWarranty || ''}
                          onChange={(v) => handleInputChange('installationWarranty', v ? parseInt(v) : null)}
                          placeholder="e.g., 1"
                          className={getWarrantyError(newShowerType.installationWarranty) ? 'border-red-500' : ''}
                          data-testid="shower-installation-warranty-input"
                        />
                        {getWarrantyError(newShowerType.installationWarranty) && <p className="text-xs text-red-500">{getWarrantyError(newShowerType.installationWarranty)}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Picture Upload - At Bottom */}
                  <div className="space-y-4">
                    <h4 className="font-semibold border-b pb-2">Shower Type Picture</h4>
                    
                    {/* Show existing picture for edit mode */}
                    {editingShowerType && (showerTypePictureUrl) && !pendingShowerTypePicture && (
                      <div className="mb-4" key={showerTypePictureUrl}>
                        <img
                          src={showerTypePictureUrl}
                          alt="Shower Type"
                          className="max-w-full h-auto max-h-64 border rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextSibling;
                            if (placeholder) placeholder.style.display = 'block';
                          }}
                        />
                        <div 
                          style={{ display: 'none' }} 
                          className="p-8 bg-slate-100 border-2 border-dashed rounded text-center text-slate-500"
                        >
                          <p>Image not found or failed to load</p>
                          <p className="text-sm mt-2">Please upload a new picture</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Show pending picture preview */}
                    {pendingShowerTypePicture?.previewUrl && (
                      <div className="mb-4">
                        <div className="relative inline-block">
                          <img
                            src={pendingShowerTypePicture.previewUrl}
                            alt="Preview"
                            className="max-w-full h-auto max-h-64 border rounded"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => {
                              if (pendingShowerTypePicture?.previewUrl) {
                                URL.revokeObjectURL(pendingShowerTypePicture.previewUrl);
                              }
                              setPendingShowerTypePicture(null);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        {!editingShowerType && (
                          <p className="text-sm text-green-600 mt-2">Image ready - will be uploaded when shower type is created</p>
                        )}
                      </div>
                    )}
                    
                    {/* Action buttons - always visible */}
                    <div className="space-y-2">
                      <Label>Upload Picture (PNG/JPEG/PDF)</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handlePasteFromClipboardInModal}
                          title="Paste image from clipboard"
                          disabled={showerTypePictureUploading === editingShowerType?.id}
                        >
                          {showerTypePictureUploading === editingShowerType?.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <ClipboardPaste className="h-4 w-4 mr-2" />
                          )}
                          Paste
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('shower-type-picture-modal').click()}
                          disabled={showerTypePictureUploading === editingShowerType?.id}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {(showerTypePictureUrl || pendingShowerTypePicture) ? 'Change Picture' : 'Upload Picture'}
                        </Button>
                        {showerTypePictureUrl && editingShowerType && handleDeleteShowerTypePicture && (
                          <Button
                            type="button"
                            variant="destructive"
                            onClick={() => {
                              setShowerTypePictureUrl(null);
                              handleDeleteShowerTypePicture(editingShowerType.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Picture
                          </Button>
                        )}
                        <input
                          id="shower-type-picture-modal"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const previewUrl = URL.createObjectURL(file);
                              setPendingShowerTypePicture({ file, previewUrl });
                            }
                            e.target.value = '';
                          }}
                          style={{ display: 'none' }}
                        />
                      </div>
                      {!editingShowerType && !pendingShowerTypePicture && (
                        <p className="text-sm text-slate-500">Picture will be uploaded after creating the type</p>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={operationLoading || !isFormValid()}
                      data-testid="save-shower-type-btn"
                    >
                      {operationLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        editingShowerType ? 'Update' : 'Create'
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* List */}
      {showerTypes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">No shower types created yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {showerTypes.map((type) => (
            <Card key={type.id} data-testid={`shower-type-${type.id}`} className={selectedShowerTypeIds.includes(type.id) ? 'ring-2 ring-blue-500' : ''}>
              <div className="flex">
                {/* Checkbox column */}
                {canEditCSIItems && (
                  <div className="w-12 flex-shrink-0 flex items-start justify-center pt-4 border-r">
                    <Checkbox
                      checked={selectedShowerTypeIds.includes(type.id)}
                      onCheckedChange={(checked) => toggleShowerTypeSelection(type.id, { target: { checked } })}
                      className="h-5 w-5"
                    />
                  </div>
                )}
                
                {/* Picture column - Left */}
                <div className="flex-shrink-0 p-4 border-r" style={{ width: '250px', height: '300px' }}>
                  {(type.pictureCloudinary?.urls?.medium || type.pictureUrl) ? (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        <div className="flex gap-1">
                          {canUserEdit && canUserEdit(type.createdBy) && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPasteFromClipboard(type.id)}
                                title="Paste image from clipboard"
                                disabled={showerTypePictureUploading === type.id}
                              >
                                {showerTypePictureUploading === type.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ClipboardPaste className="h-3 w-3" />
                                )}
                              </Button>
                              <label className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild>
                                  <span><Upload className="h-3 w-3" /></span>
                                </Button>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/png,image/jpeg,image/jpg,application/pdf"
                                  onChange={(e) => onPictureUpload(e, type.id)}
                                />
                              </label>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteShowerTypePicture(type.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        className="flex-1 cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center overflow-hidden"
                        onClick={() => onViewDrawing({
                          url: type.pictureCloudinary?.urls?.large || type.pictureUrl,
                          title: `${type.name} - Picture`
                        })}
                      >
                        <img
                          src={type.pictureCloudinary?.urls?.medium || type.pictureUrl}
                          alt="Shower Type"
                          className="max-w-full max-h-full object-contain border rounded"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        {canUserEdit && canUserEdit(type.createdBy) && (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onPasteFromClipboard(type.id)}
                              title="Paste image from clipboard"
                              disabled={showerTypePictureUploading === type.id}
                            >
                              {showerTypePictureUploading === type.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <ClipboardPaste className="h-3 w-3" />
                              )}
                            </Button>
                            <label className="cursor-pointer">
                              <Button variant="outline" size="sm" asChild>
                                <span><Upload className="h-3 w-3" /></span>
                              </Button>
                              <input
                                type="file"
                                className="hidden"
                                accept="image/png,image/jpeg,image/jpg,application/pdf"
                                onChange={(e) => onPictureUpload(e, type.id)}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        {canUserEdit && canUserEdit(type.createdBy) ? (
                          <label className="cursor-pointer w-full">
                            <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-slate-50 transition-colors">
                              <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                              <p className="text-sm text-slate-600">Upload Picture</p>
                              <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/png,image/jpeg,image/jpg,application/pdf"
                              onChange={(e) => onPictureUpload(e, type.id)}
                            />
                          </label>
                        ) : (
                          <div className="text-center text-slate-400">
                            <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                            <p className="text-sm">No Picture</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Content column - Right */}
                <div className="flex-1 p-4 flex flex-col justify-between" style={{ height: '300px' }}>
                  {/* Action buttons */}
                  {canUserEdit && canUserEdit(type.createdBy) && (
                    <div className="flex gap-2 items-center justify-end mb-2">
                      <ColorPickerPopover
                        id={`shower-${type.id}`}
                        currentColor={type.markerColor || DEFAULT_MARKER_COLOR}
                        pendingColor={type.pendingMarkerColor}
                        isOpen={type.colorPickerOpen}
                        isSaving={type.savingColor}
                        onOpenChange={(open) => handleColorPickerOpenChange(type.id, open)}
                        onColorChange={(newColor) => handleColorChange(type.id, newColor)}
                        onSave={() => handleColorSave(type)}
                      />
                      <Button variant="outline" size="sm" onClick={() => openEditShowerTypeDialog(type)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteShowerType(type.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* 4 Rows of content - fixed width columns */}
                  <div className="flex-1 flex flex-col justify-evenly text-sm">
                    {/* Row 1: Name, Manufacturer, Model */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Type</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{type.name}</div>
                      <div className="font-semibold w-[95px]">Manufacturer</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{type.manufacturer}</div>
                      <div className="font-semibold w-[95px]">Model</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{type.model}</div>
                    </div>
                    
                    {/* Row 2: Operation Type */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Operation</div>
                      <div className="w-[10px]">:</div>
                      <div className="flex-1">{type.operationType || 'N/A'}</div>
                    </div>
                    
                    {/* Row 3: Finish Warranty, Glass Warranty, Hardware Warranty */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Finish Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{type.finishWarranty ? `${type.finishWarranty} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Glass Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{type.glassWarranty ? `${type.glassWarranty} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Hdw. Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{type.hardwareWarranty ? `${type.hardwareWarranty} Years` : 'N/A'}</div>
                    </div>
                    
                    {/* Row 4: Installation Warranty */}
                    <div className="flex">
                      <div className="font-semibold w-[95px]">Install Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="flex-1">{type.installationWarranty ? `${type.installationWarranty} Years` : 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShowerTypesSection;
